// ShipMarkers.tsx — Renders every ship's galaxy-map icon. Like SystemShips, this is
// the lone component that subscribes to the high-frequency sim snapshot, so the parent
// Galaxy (which maps ~220 StarMarkers) never re-renders on a snapshot.
//
// Empire ships sitting in a system are grouped into ONE owner-coloured fleet marker per
// (system, empire) — a late-game galaxy holds hundreds of warships, and a label per hull
// would bury the map. Ships mid-jump stay individual so you can watch fleets move.
import type { Galaxy as GalaxyData } from '../types';
import { useMatch, useSim } from '../state/context';
import { isEmpireShip, ownerColor, type Ship } from '../state/economy';
import { ShipMarker } from './ShipMarker';

interface Props {
  galaxy: GalaxyData;
}

export function ShipMarkers({ galaxy }: Props) {
  const sim = useSim();
  const match = useMatch();
  if (!sim) return null;
  const singles: Ship[] = [];
  const fleets = new Map<string, Ship[]>();
  for (const s of sim.ships ?? []) {
    if (isEmpireShip(s) && s.phase !== 'hyperlane') {
      const k = `${s.systemId}|${s.owner}`;
      const list = fleets.get(k);
      if (list) list.push(s);
      else fleets.set(k, [s]);
    } else {
      singles.push(s);
    }
  }
  return (
    <>
      {singles.map((s) => {
        // In a match the map is about empires: civilian freighters and rivals' ships in
        // flight are bare icons; only your own ships keep a nametag.
        const empire = isEmpireShip(s);
        const label = !match ? undefined : empire && s.owner === match.you ? undefined : '';
        return <ShipMarker key={s.id} galaxy={galaxy} ship={s} tint={empire ? ownerColor(sim, s.owner) : undefined} label={label} />;
      })}
      {[...fleets.entries()].map(([k, ships]) => {
        const lead = ships[0];
        const mine = !!match?.you && lead.owner === match.you;
        return (
          <ShipMarker
            key={k}
            galaxy={galaxy}
            ship={lead}
            tint={ownerColor(sim, lead.owner)}
            label={mine ? `▸ ${ships.length} · your fleet` : `▸ ${ships.length}`}
          />
        );
      })}
    </>
  );
}
